import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Modal } from '../../design-system/Modal';
import { Input, Textarea } from '../../design-system/Input';
import { Select } from '../../design-system/Dropdown';
import { Button } from '../../design-system/Button';
import { CheckCircle2, AlertCircle, Clock } from 'lucide-react';

export const QuickAddTaskModal = ({ isOpen, onClose }) => {
  const { addTask, activeProject, projects, setActiveProjectId } = useWorkspace();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('todo');
  const [priority, setPriority] = useState('medium');
  const [estimate, setEstimate] = useState('3 pts');
  const [error, setError] = useState('');

  const statusOptions = [
    { value: 'backlog', label: 'Backlog', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>inventory_2</span> },
    { value: 'todo', label: 'Todo', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>radio_button_unchecked</span> },
    { value: 'in_progress', label: 'In Progress', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px', color: 'var(--accent-teal)' }}>pending</span> },
    { value: 'review', label: 'Review', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px', color: 'var(--color-purple)' }}>rate_review</span> },
    { value: 'done', label: 'Done', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px', color: 'var(--color-success)' }}>check_circle</span> }
  ];

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent', icon: <AlertCircle size={14} color="var(--color-danger)" /> },
    { value: 'high', label: 'High', icon: <AlertCircle size={14} color="var(--color-warning)" /> },
    { value: 'medium', label: 'Medium', icon: <Clock size={14} color="var(--color-info)" /> },
    { value: 'low', label: 'Low', icon: <CheckCircle2 size={14} color="var(--text-muted)" /> }
  ];

  const projectOptions = projects.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.key})`
  }));

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!title.trim()) {
      setError('Task title is required');
      return;
    }

    addTask({
      title: title.trim(),
      description: description.trim(),
      status,
      priority,
      estimate,
      assignee: { name: 'Rohith Prince', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80' }
    });

    setTitle('');
    setDescription('');
    setStatus('todo');
    setPriority('medium');
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Task"
      subtitle={`Adding to project: ${activeProject?.name || 'Current Project'}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Create Task
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Project Selector */}
        <Select
          label="Target Project"
          options={projectOptions}
          value={activeProject?.id}
          onChange={(val) => setActiveProjectId(val)}
        />

        {/* Title */}
        <Input
          label="Task Title *"
          placeholder="e.g., Build neomorphic buttons & dark theme support"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (error) setError('');
          }}
          error={error}
          autoFocus
        />

        {/* Description */}
        <Textarea
          label="Description"
          placeholder="Add context, acceptance criteria, or technical notes..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />

        {/* Grid for Status & Priority */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Select
            label="Initial Status"
            options={statusOptions}
            value={status}
            onChange={(val) => setStatus(val)}
          />

          <Select
            label="Priority Level"
            options={priorityOptions}
            value={priority}
            onChange={(val) => setPriority(val)}
          />
        </div>

        {/* Estimate */}
        <Input
          label="Story Points / Estimate"
          placeholder="e.g., 3 pts, 2 days"
          value={estimate}
          onChange={(e) => setEstimate(e.target.value)}
        />
      </form>
    </Modal>
  );
};
